import { useEffect, useMemo, useRef, useState } from 'react';
import { HexColorInput } from 'react-colorful';
import { env } from '../../config';
import { PopoverPicker } from '../ColorPicker/PopoverPicker';
import { getSlideById } from '../Screen/utils';
import brandingPhoto from '../../images/photos/branding.png';
import constants from '../Screen/constants.json';

const DEFAULT_BRANDING = constants.DEFAULT_BRANDING_CONFIGS;

const BrandingConfiguration = ({ playlist, onChange, handleContentModalOpen, permissions }) => {
    const [backgroundSlide, setBackgroundSlide] = useState({});
    const branding = { ...DEFAULT_BRANDING, ...playlist?.brandingConfigs };

    useEffect(() => {
        if (playlist?.brandingConfigs?.backgroundImageId) {
            getSlideById(playlist.brandingConfigs.backgroundImageId)
                .then(slide => setBackgroundSlide(slide))
                .catch(() => setBackgroundSlide({}));
        } else {
            setBackgroundSlide({});
        }
    }, [playlist?.id]);

    const handleColor = (field, value) => {
        const color = value.length > 6 ? value.slice(1) : value;
        onChange(field, color);
    };

    const openImagePicker = () => {
        handleContentModalOpen(
            'shared_folder',
            null,
            null,
            (slide) => {
                if (slide.id) {
                    const mainBg = (branding.mainBg || '').slice(0, 6) + '00';
                    onChange('backgroundImageId', slide.id);
                    onChange('mainBg', mainBg);
                    setBackgroundSlide(slide);
                }
            }
        );
    };

    const isEditable = useMemo(() => {
        let retval = false;
        if (
            playlist?.id &&
            playlist?.location?.id in permissions['journey'] &&
            playlist?.location?.id in permissions['journeyBrandingConfiguration'] &&
            permissions['journeyBrandingConfiguration'][playlist.location.id].includes('editor')
        ) {
            retval = true;
        }

        return retval;
    }, [playlist]);

    return (
        <div className="JourneyBrandingConfiguration">
            <div className="photo">
                <img src={brandingPhoto} alt="branding" />
            </div>
            <div className="branding-config">
                <div className="branding-left">
                    <h3 className="branding-title">Branding</h3>
                    <h4>Primary colors and style</h4>
                    <p className="branding-description">
                        Adjust the background color and image opacity to lighten or darken the image.
                        As you reduce the opacity, the image will become more transparent and reveal the background color.
                    </p>
                    <div className="input-container" style={{ opacity: 0.4, pointerEvents: 'none' }}>
                        <div className="label">Background:</div>
                        <HexColorInput
                            className="input-color"
                            color={branding.mainBg}
                            onChange={val => handleColor('mainBg', val)}
                            prefixed={true}
                            alpha={true}
                        />
                        <PopoverPicker
                            color={'#' + branding.mainBg}
                            onChange={val => handleColor('mainBg', val)}
                            alpha={true}
                        />
                    </div>
                    <div className="input-container" style={{ opacity: 0.4, pointerEvents: 'none' }}>
                        <div className="label">Opacity (%):</div>
                        <input
                            className="opacity-input"
                            type="number"
                            min={0}
                            max={100}
                            value={branding.backgroundOpacity ?? 100}
                            onChange={e => onChange('backgroundOpacity', Number(e.target.value))}
                        />
                    </div>
                </div>
                <div className="branding-right">
                    <div className="image-placeholder" onClick={isEditable ? openImagePicker : null} style={!isEditable ? { pointerEvents: "none", cursor: "default"} : null}>
                        {backgroundSlide.url ? (
                            <BackgroundPreview
                                url={backgroundSlide.url}
                                onRemove={e => {
                                    e.stopPropagation();
                                    onChange('backgroundImageId', null);
                                    setBackgroundSlide({});
                                }}
                                isEditable={isEditable}
                            />
                        ) : (
                            <>
                                <div className="placeholder-icon">
                                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                        <rect x="8" y="8" width="32" height="32" rx="2" stroke="#CCD2D3" strokeWidth="2" strokeDasharray="4 2" fill="none"/>
                                        <rect x="16" y="16" width="16" height="16" rx="1" stroke="#CCD2D3" strokeWidth="1.5" fill="none"/>
                                        <line x1="24" y1="8" x2="24" y2="14" stroke="#CCD2D3" strokeWidth="2"/>
                                        <line x1="24" y1="34" x2="24" y2="40" stroke="#CCD2D3" strokeWidth="2"/>
                                        <line x1="8" y1="24" x2="14" y2="24" stroke="#CCD2D3" strokeWidth="2"/>
                                        <line x1="34" y1="24" x2="40" y2="24" stroke="#CCD2D3" strokeWidth="2"/>
                                    </svg>
                                </div>
                                <p className="placeholder-text">Click here to select<br/>background image</p>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const BackgroundPreview = ({ url, onRemove, isEditable }) => {
    const ref = useRef();
    const isImage = url.includes(env.S3_MESSAGING_BUCKET);
    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {isImage
                ? <img src={url} ref={ref} className="background-image" alt="background" />
                : <iframe src={url} ref={ref} className="background-image" title="background preview" />
            }
            {
                isEditable ?
                <button
                    onClick={onRemove}
                    style={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#e03131',
                        fontSize: 18,
                        fontWeight: 'bold',
                        lineHeight: 1,
                        padding: 0,
                    }}
                >
                    ✕
                </button>
                :
                <></>
            }
        </div>
    );
};

export default BrandingConfiguration;
